import React, { useState, useEffect, useContext } from "react";
import {
  getAllPromoCodes,
  getPromoCodeSettings,
  updatePromoCodeSettings,
  updatePromoCodeDiscount,
  updatePromoCodeReward,
  updatePromoCodeStatus,
  updatePromoCodesByRole,
  resetUserRewards,
  PromoCodeDTO,
  PromoCodeSettings,
} from "../services/promo-code-service";
import CustomInput from "../shared/custom-input/custom-input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faEdit, 
  faSave, 
  faTimes, 
  faCoins, 
  faPercent, 
  faUsers, 
  faCircleCheck, 
  faCircleXmark, 
  faTrashRestore, 
  faFilter,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faWandMagicSparkles,
  faLock,
  faLockOpen,
  faRotateLeft
} from "@fortawesome/free-solid-svg-icons";
import { SnackbarContext } from "../config/hooks/use-toast";

const AdminPromoCodeManagement: React.FC = () => {
  const snackbarContext = useContext(SnackbarContext);
  const [promoCodes, setPromoCodes] = useState<PromoCodeDTO[]>([]);
  const [settings, setSettings] = useState<PromoCodeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingPromoId, setEditingPromoId] = useState<string | null>(null);
  const [newDefaultDiscount, setNewDefaultDiscount] = useState("");
  const [newDefaultCoins, setNewDefaultCoins] = useState("");
  const [editDiscount, setEditDiscount] = useState("");
  const [editReward, setEditReward] = useState("");
  const [savingPromoId, setSavingPromoId] = useState<string | null>(null);
  const [togglingPromoId, setTogglingPromoId] = useState<string | null>(null);
  const [bulkRole, setBulkRole] = useState("ROLE_STUDENT");
  const [bulkDiscount, setBulkDiscount] = useState("");
  const [bulkReward, setBulkReward] = useState("");
  const [bulkStatus, setBulkStatus] = useState<"none" | "activate" | "deactivate">("none");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [roleFilter, setRoleFilter] = useState<string>("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [codesData, settingsData] = await Promise.all([
        getAllPromoCodes(),
        getPromoCodeSettings(),
      ]);
      setPromoCodes(codesData);
      setSettings(settingsData);
      setNewDefaultDiscount(settingsData.defaultDiscountPercentage.toString());
      setNewDefaultCoins(settingsData.defaultCoinsReward.toString());
    } catch (error) {
      console.error("Failed to fetch promo code data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async () => {
    if (!settings) return;

    try {
      const updated = await updatePromoCodeSettings(
        parseFloat(newDefaultDiscount),
        parseInt(newDefaultCoins)
      );
      setSettings(updated);
      setEditingSettings(false);
      snackbarContext?.showMessage("Succès", "Paramètres mis à jour avec succès !", "success");
    } catch (error) {
      console.error("Failed to update settings:", error);
      snackbarContext?.showMessage("Erreur", "Échec de la mise à jour des paramètres", "error");
    }
  };

  const handleSavePromo = async (promoCodeId: string) => {
    const target = promoCodes.find((pc) => pc.id === promoCodeId);
    if (!target) return;

    const discountValue = parseFloat(editDiscount);
    const rewardValue = parseInt(editReward, 10);

    if (Number.isNaN(discountValue) || Number.isNaN(rewardValue)) {
      snackbarContext?.showMessage("Attention", "Valeurs invalides", "warning");
      return;
    }

    setSavingPromoId(promoCodeId);
    try {
      let updated = target;
      if (discountValue !== target.discountPercentage) {
        updated = await updatePromoCodeDiscount(promoCodeId, discountValue);
      }
      if (rewardValue !== updated.coinsReward) {
        updated = await updatePromoCodeReward(promoCodeId, rewardValue);
      }

      setPromoCodes(
        promoCodes.map((pc) => (pc.id === promoCodeId ? updated : pc))
      );
      setEditingPromoId(null);
      setEditDiscount("");
      setEditReward("");
      snackbarContext?.showMessage("Succès", "Code promo mis à jour !", "success");
    } catch (error) {
      console.error("Failed to update promo code:", error);
      snackbarContext?.showMessage("Erreur", "Échec de la mise à jour", "error");
    } finally {
      setSavingPromoId(null);
    }
  };

  const handleToggleStatus = async (promoCodeId: string, active: boolean) => {
    setTogglingPromoId(promoCodeId);
    try {
      const updated = await updatePromoCodeStatus(promoCodeId, active);
      setPromoCodes(
        promoCodes.map((pc) => (pc.id === promoCodeId ? updated : pc))
      );
      snackbarContext?.showMessage("Succès", `Code promo ${active ? 'activé' : 'bloqué'}`, "success");
    } catch (error) {
      console.error("Failed to update promo code status:", error);
      snackbarContext?.showMessage("Erreur", "Échec de la mise à jour du statut", "error");
    } finally {
      setTogglingPromoId(null);
    }
  };

  const handleResetRewards = async (promo: PromoCodeDTO) => {
    const isTeacherRole =
      promo.ownerRole === "ROLE_TEACHER" || promo.ownerRole === "ROLE_SUPER_TEACHER";
    const confirmReset = window.confirm(
      `Remettre les récompenses à 0 pour ${promo.ownerName} ?` +
        (isTeacherRole ? " (Inclure les points enseignants ?)" : "")
    );
    if (!confirmReset) return;

    const resetTeacherPoints = isTeacherRole ? true : false;
    setResettingUserId(promo.ownerId);
    try {
      await resetUserRewards(promo.ownerId, resetTeacherPoints);
      snackbarContext?.showMessage("Succès", "Récompenses remises à zéro", "success");
      fetchData(); // Refresh list to reflect coins change
    } catch (error) {
      console.error("Failed to reset rewards:", error);
      snackbarContext?.showMessage("Erreur", "Échec de la remise à zéro", "error");
    } finally {
      setResettingUserId(null);
    }
  };

  const handleBulkUpdate = async () => {
    const discountValue = bulkDiscount.trim() === "" ? undefined : parseFloat(bulkDiscount);
    const rewardValue = bulkReward.trim() === "" ? undefined : parseInt(bulkReward, 10);
    const activeValue =
      bulkStatus === "none" ? undefined : bulkStatus === "activate" ? true : false;

    if (
      discountValue === undefined &&
      rewardValue === undefined &&
      activeValue === undefined
    ) {
      snackbarContext?.showMessage("Info", "Définissez au moins un champ à mettre à jour", "info");
      return;
    }

    setBulkLoading(true);
    try {
      const updated = await updatePromoCodesByRole(
        bulkRole,
        discountValue,
        rewardValue,
        activeValue
      );

      if (updated && updated.length > 0) {
        const updatedMap = new Map(updated.map((pc) => [pc.id, pc]));
        setPromoCodes((prev) =>
          prev.map((pc) => (updatedMap.has(pc.id) ? (updatedMap.get(pc.id) as PromoCodeDTO) : pc))
        );
      }
      snackbarContext?.showMessage("Succès", "Mise à jour globale appliquée !", "success");
    } catch (error) {
      console.error("Failed to bulk update promo codes:", error);
      snackbarContext?.showMessage("Erreur", "Échec de la mise à jour globale", "error");
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredPromoCodes = promoCodes.filter((promo) => {
    if (roleFilter === "all") return true;
    return promo.ownerRole === roleFilter;
  });

  const totalPages = Math.ceil(filteredPromoCodes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPromoCodes = filteredPromoCodes.slice(startIndex, endIndex);

  const roleLabels: Record<string, string> = {
    ROLE_TEACHER: "Professeur",
    ROLE_STUDENT: "Élève",
    ROLE_ADMIN: "Administrateur",
    ROLE_SUPER_TEACHER: "Super Prof",
  };

  const availableRoles = Array.from(
    new Set(promoCodes.map((promo) => promo.ownerRole).filter(Boolean))
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
           <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
           <p className="text-[10px] font-black uppercase tracking-widest text-text/40">Synchronisation des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8 bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <div className="mx-auto max-w-7xl space-y-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-4">
          <div className="space-y-2">
            <h1 className="text-4xl font-black text-title uppercase tracking-tight">
              Gestion Codes Promo
            </h1>
            <p className="text-[11px] font-bold text-text/40 uppercase tracking-[0.3em] flex items-center gap-2">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="text-primary" />
              Incitations & Récompenses Système
            </p>
          </div>
          <div className="flex items-center gap-3">
             <div className="bg-white/70 backdrop-blur-xl border border-white px-5 py-3 rounded-2xl shadow-xl shadow-black/5">
                <span className="text-[10px] font-black text-text/30 uppercase tracking-widest mr-2">Total Codes:</span>
                <span className="text-lg font-black text-title">{promoCodes.length}</span>
             </div>
          </div>
        </div>

        {/* Hero Stats Section - Paramètres */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className={`col-span-1 lg:col-span-2 group relative overflow-hidden bg-white/70 backdrop-blur-2xl rounded-[3rem] border border-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] p-10 transition-all duration-500`}>
              {/* Decorative Glow - MUST HAVE pointer-events-none */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-transparent blur-3xl -mr-20 -mt-20 opacity-40 group-hover:opacity-60 transition-opacity pointer-events-none"></div>
              
              <div className="flex items-center justify-between mb-8">
                 <h2 className="text-xl font-black text-title uppercase tracking-tight">Paramètres par défaut</h2>
                 {!editingSettings ? (
                    <button
                        type="button"
                        onClick={() => {
                            if (settings) {
                                setNewDefaultDiscount(settings.defaultDiscountPercentage.toString());
                                setNewDefaultCoins(settings.defaultCoinsReward.toString());
                            }
                            setEditingSettings(true);
                        }}
                        className="w-12 h-12 flex items-center justify-center rounded-2xl bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/20 cursor-pointer"
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                 ) : (
                    <div className="flex gap-3">
                         <button 
                            type="button"
                            onClick={handleUpdateSettings} 
                            className="w-12 h-12 flex items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 hover:scale-110 transition-all cursor-pointer"
                         >
                             <FontAwesomeIcon icon={faSave} />
                         </button>
                         <button 
                            type="button"
                            onClick={() => {
                                if (settings) {
                                    setNewDefaultDiscount(settings.defaultDiscountPercentage.toString());
                                    setNewDefaultCoins(settings.defaultCoinsReward.toString());
                                }
                                setEditingSettings(false);
                            }} 
                            className="w-12 h-12 flex items-center justify-center rounded-2xl bg-rose-500 text-white shadow-lg shadow-rose-500/20 hover:scale-110 transition-all cursor-pointer"
                         >
                             <FontAwesomeIcon icon={faTimes} />
                         </button>
                    </div>
                 )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 {editingSettings ? (
                   <>
                     <div className="space-y-3">
                        <label className="text-[10px] font-black text-text/40 uppercase tracking-widest ml-2">Réduction (%)</label>
                        <CustomInput
                            inputType="number"
                            value={newDefaultDiscount}
                            onChange={(e) => setNewDefaultDiscount(e.target.value)}
                            inputStyle="!h-16 !bg-gray-50/50 !border-gray-100 !rounded-[1.5rem] !font-black !text-title !px-6"
                        />
                     </div>
                     <div className="space-y-3">
                        <label className="text-[10px] font-black text-text/40 uppercase tracking-widest ml-2">Récompense (Pièces)</label>
                        <CustomInput
                            inputType="number"
                            value={newDefaultCoins}
                            onChange={(e) => setNewDefaultCoins(e.target.value)}
                            inputStyle="!h-16 !bg-gray-50/50 !border-gray-100 !rounded-[1.5rem] !font-black !text-title !px-6"
                        />
                     </div>
                   </>
                 ) : (
                   <>
                     <div className="p-8 rounded-[2rem] bg-gradient-to-br from-blue-500/5 to-blue-600/10 border border-blue-100/50 flex items-center gap-6">
                        <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center text-white shadow-xl shadow-blue-500/20">
                           <FontAwesomeIcon icon={faPercent} className="text-2xl" />
                        </div>
                        <div>
                           <p className="text-[10px] font-black text-blue-600/50 uppercase tracking-widest">Réduction Standard</p>
                           <h3 className="text-4xl font-black text-blue-600 tracking-tighter tabular-nums">{settings?.defaultDiscountPercentage}%</h3>
                        </div>
                     </div>
                     <div className="p-8 rounded-[2rem] bg-gradient-to-br from-amber-500/5 to-amber-600/10 border border-amber-100/50 flex items-center gap-6">
                        <div className="w-16 h-16 rounded-2xl bg-amber-500 flex items-center justify-center text-white shadow-xl shadow-amber-500/20">
                           <FontAwesomeIcon icon={faCoins} className="text-2xl" />
                        </div>
                        <div>
                           <p className="text-[10px] font-black text-amber-600/50 uppercase tracking-widest">Récompense Alpha</p>
                           <h3 className="text-4xl font-black text-amber-600 tracking-tighter tabular-nums">{settings?.defaultCoinsReward}</h3>
                        </div>
                     </div>
                   </>
                 )}
              </div>
            </div>

            {/* Quick Filter Bento */}
            <div className="bg-title rounded-[3rem] p-10 flex flex-col justify-between overflow-hidden relative shadow-2xl shadow-title/20">
                <div className="relative z-10">
                   <h2 className="text-xl font-black text-white uppercase tracking-tight mb-2">Filtre Rapide</h2>
                   <p className="text-[10px] font-bold text-white/40 uppercase tracking-[0.2em]">Ciblage précis par profil</p>
                </div>
                
                <div className="relative z-10 mt-8 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                       {['all', ...availableRoles].map((role) => (
                         <button
                            key={role}
                            onClick={() => setRoleFilter(role)}
                            className={`h-14 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${roleFilter === role ? 'bg-primary text-white shadow-lg' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                         >
                            {role === 'all' ? 'Tous' : roleLabels[role] || role.replace('ROLE_', '')}
                         </button>
                       ))}
                    </div>
                </div>

                {/* Aesthetic Pattern - MUST HAVE pointer-events-none */}
                <div className="absolute bottom-[-20%] right-[-10%] w-48 h-48 rounded-full border-[20px] border-white/5 opacity-40 pointer-events-none"></div>
            </div>
        </div>

        {/* Global Action Bento UI */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-[3rem] border border-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] p-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                  <h2 className="text-xl font-black text-title uppercase tracking-tight">Mise à jour globale</h2>
                  <p className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em] mt-1">Appliquer des modifications massives par rôle</p>
                </div>
                <div className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${bulkLoading ? 'bg-amber-100 text-amber-600 animate-pulse' : 'bg-primary/5 text-primary'}`}>
                    {bulkLoading ? 'Opération en cours...' : 'Prêt pour traitement'}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-6">
                <div className="space-y-3">
                    <label className="text-[10px] font-black text-text/30 uppercase tracking-widest ml-2">Cible Rôle</label>
                    <select
                        className="w-full h-16 px-6 bg-gray-50/50 border border-gray-100 rounded-2xl font-black text-title text-[11px] uppercase tracking-wider outline-none focus:border-primary/30 transition-all appearance-none cursor-pointer"
                        value={bulkRole}
                        onChange={(e) => setBulkRole(e.target.value)}
                    >
                        <option value="ROLE_STUDENT">Élèves</option>
                        <option value="ROLE_TEACHER">Professeurs</option>
                        <option value="ROLE_SUPER_TEACHER">Super Professeurs</option>
                    </select>
                </div>
                <div className="space-y-3">
                    <label className="text-[10px] font-black text-text/30 uppercase tracking-widest ml-2">Nouvelle Réduc %</label>
                    <CustomInput
                        inputType="number"
                        value={bulkDiscount}
                        onChange={(e) => setBulkDiscount(e.target.value)}
                        placeholder="Inchangé"
                        inputStyle="!h-16 !bg-gray-50/50 !border-gray-100 !rounded-2xl !font-black !text-title !px-6"
                    />
                </div>
                <div className="space-y-3">
                    <label className="text-[10px] font-black text-text/30 uppercase tracking-widest ml-2">Nouvelle Récomp.</label>
                    <CustomInput
                        inputType="number"
                        value={bulkReward}
                        onChange={(e) => setBulkReward(e.target.value)}
                        placeholder="Inchangé"
                        inputStyle="!h-16 !bg-gray-50/50 !border-gray-100 !rounded-2xl !font-black !text-title !px-6"
                    />
                </div>
                <div className="space-y-3">
                    <label className="text-[10px] font-black text-text/30 uppercase tracking-widest ml-2">Action Statut</label>
                    <select
                        className="w-full h-16 px-6 bg-gray-50/50 border border-gray-100 rounded-2xl font-black text-title text-[11px] uppercase tracking-wider outline-none focus:border-primary/30 transition-all appearance-none cursor-pointer"
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value as any)}
                    >
                        <option value="none">Ne pas changer</option>
                        <option value="activate">Tout Activer</option>
                        <option value="deactivate">Tout Bloquer</option>
                    </select>
                </div>
                <div className="flex items-end">
                    <button
                        onClick={handleBulkUpdate}
                        disabled={bulkLoading}
                        className={`w-full h-16 rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] transition-all duration-500 shadow-2xl flex items-center justify-center gap-3 ${bulkLoading ? 'bg-gray-100 text-gray-400' : 'bg-primary text-white shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1'}`}
                    >
                        <span>Appliquer</span>
                        <FontAwesomeIcon icon={faWandMagicSparkles} />
                    </button>
                </div>
            </div>
        </div>

        {/* Main Records Table Card */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-[3rem] border border-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.1)] overflow-hidden">
           <div className="p-10 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-title uppercase tracking-tight">Registres des Codes</h2>
                <p className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em] mt-1">Audit détaillé et contrôle individuel</p>
              </div>
              <div className="flex items-center gap-4">
                 <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="h-10 px-4 bg-gray-50 rounded-xl font-black text-[10px] uppercase tracking-widest text-text/40 border-none outline-none focus:ring-2 focus:ring-primary/20 appearance-none cursor-pointer"
                    >
                    <option value="10">10 / Page</option>
                    <option value="20">20 / Page</option>
                    <option value="50">50 / Page</option>
                 </select>
              </div>
           </div>

           <div className="overflow-x-auto no-scrollbar">
              <table className="w-full border-separate border-spacing-y-2 px-10 pb-10">
                 <thead>
                    <tr>
                       <th className="px-6 py-4 text-[10px] font-black text-text/30 uppercase tracking-[0.2em] text-left">Identifiant Code</th>
                       <th className="px-6 py-4 text-[10px] font-black text-text/30 uppercase tracking-[0.2em] text-left">Propriétaire & Rôle</th>
                       <th className="px-6 py-4 text-[10px] font-black text-text/30 uppercase tracking-[0.2em] text-center">Incitation</th>
                       <th className="px-6 py-4 text-[10px] font-black text-text/30 uppercase tracking-[0.2em] text-center">Performance</th>
                       <th className="px-6 py-4 text-[10px] font-black text-text/30 uppercase tracking-[0.2em] text-center">Statut</th>
                       <th className="px-6 py-4 text-[10px] font-black text-text/30 uppercase tracking-[0.2em] text-right">Contrôle</th>
                    </tr>
                 </thead>
                 <tbody>
                    {paginatedPromoCodes.map((promo) => (
                       <tr key={promo.id} className="group transition-all">
                          <td className="px-6 py-4 bg-white/50 group-hover:bg-white rounded-l-[1.5rem] transition-all duration-300">
                             <div className="flex flex-col">
                                <span className="font-black text-sm text-primary uppercase tracking-tighter">{promo.code}</span>
                                <span className="text-[9px] font-bold text-text/20 uppercase tracking-widest">ID: {promo.id.substring(0,8)}...</span>
                             </div>
                          </td>
                          <td className="px-6 py-4 bg-white/50 group-hover:bg-white transition-all duration-300">
                             <div className="flex flex-col">
                                <span className="font-black text-title text-[12px] uppercase">{promo.ownerName}</span>
                                <span className="inline-flex items-center gap-1 text-[9px] font-black text-purple-600 uppercase tracking-widest bg-purple-50 px-2 py-0.5 rounded-md mt-1 w-fit">
                                   <FontAwesomeIcon icon={faUsers} className="text-[8px]" />
                                   {roleLabels[promo.ownerRole] || promo.ownerRole || "N/A"}
                                </span>
                             </div>
                          </td>
                          <td className="px-6 py-4 bg-white/50 group-hover:bg-white transition-all duration-300">
                             {editingPromoId === promo.id ? (
                                <div className="flex items-center justify-center gap-3 scale-90">
                                   <CustomInput inputType="number" value={editDiscount} onChange={(e) => setEditDiscount(e.target.value)} CustomStyle="w-16" inputStyle="!h-10 !px-2 !font-black !text-center !rounded-xl" />
                                   <span className="text-sm font-black text-emerald-500">%</span>
                                   <CustomInput inputType="number" value={editReward} onChange={(e) => setEditReward(e.target.value)} CustomStyle="w-16" inputStyle="!h-10 !px-2 !font-black !text-center !rounded-xl" />
                                   <FontAwesomeIcon icon={faCoins} className="text-amber-500" />
                                </div>
                             ) : (
                                <div className="flex items-center justify-center gap-4">
                                   <div className="flex flex-col items-center">
                                      <span className="text-lg font-black text-emerald-600 tabular-nums">{promo.discountPercentage}%</span>
                                      <span className="text-[8px] font-black text-text/20 uppercase tracking-widest">Réduct.</span>
                                   </div>
                                   <div className="w-px h-8 bg-gray-100"></div>
                                   <div className="flex flex-col items-center">
                                      <span className="text-lg font-black text-amber-600 tabular-nums">{promo.coinsReward}</span>
                                      <span className="text-[8px] font-black text-text/20 uppercase tracking-widest">Pièces</span>
                                   </div>
                                </div>
                             )}
                          </td>
                          <td className="px-6 py-4 bg-white/50 group-hover:bg-white transition-all duration-300">
                              <div className="flex flex-col items-center">
                                 <span className="text-sm font-black text-title tracking-tight tabular-nums">{promo.usageCount} Conversions</span>
                                 <div className="flex gap-2 items-center mt-1">
                                     <FontAwesomeIcon icon={faCoins} className="text-blue-500 text-[10px]" />
                                     <span className="text-[10px] font-black text-blue-700">{promo.ownerCoins ?? 0}</span>
                                 </div>
                              </div>
                          </td>
                          <td className="px-6 py-4 bg-white/50 group-hover:bg-white transition-all duration-300">
                             <div className="flex justify-center">
                                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border-2 transition-all ${promo.isActive ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-rose-50 border-rose-100 text-rose-600'}`}>
                                   <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${promo.isActive ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                                   <span className="text-[9px] font-black uppercase tracking-widest">{promo.isActive ? "Certifié Actif" : "Code Bloqué"}</span>
                                </div>
                             </div>
                          </td>
                          <td className="px-6 py-4 bg-white/50 group-hover:bg-white rounded-r-[1.5rem] transition-all duration-300">
                             <div className="flex justify-end items-center gap-2">
                                {editingPromoId === promo.id ? (
                                   <>
                                      <button onClick={() => handleSavePromo(promo.id)} disabled={savingPromoId === promo.id} className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 hover:scale-110 transition-all">
                                         <FontAwesomeIcon icon={faSave} />
                                      </button>
                                      <button onClick={() => setEditingPromoId(null)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-500 text-white shadow-lg shadow-gray-500/20 hover:scale-110 transition-all">
                                         <FontAwesomeIcon icon={faTimes} />
                                      </button>
                                   </>
                                ) : (
                                   <>
                                      <button onClick={() => { setEditingPromoId(promo.id); setEditDiscount(promo.discountPercentage.toString()); setEditReward(promo.coinsReward.toString()); }} className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-600 hover:text-white transition-all" title="Édition Rapide">
                                         <FontAwesomeIcon icon={faEdit} className="text-xs" />
                                      </button>
                                      <button onClick={() => handleToggleStatus(promo.id, !promo.isActive)} disabled={togglingPromoId === promo.id} className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all ${promo.isActive ? 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-600 hover:text-white' : 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white'}`} title={promo.isActive ? "Bloquer l'accès" : "Réactiver"}>
                                         <FontAwesomeIcon icon={promo.isActive ? faLock : faLockOpen} className="text-xs" />
                                      </button>
                                      <button onClick={() => handleResetRewards(promo)} disabled={resettingUserId === promo.ownerId} className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 border border-amber-100 hover:bg-amber-600 hover:text-white transition-all" title="Réinitialiser Portefeuille">
                                         <FontAwesomeIcon icon={faRotateLeft} className="text-xs" />
                                      </button>
                                   </>
                                )}
                             </div>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>

           {/* Modern Pagination Section */}
           {totalPages > 1 && (
             <div className="p-10 bg-gray-50/50 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-[10px] font-black text-text/30 uppercase tracking-[0.2em]">
                   Affichage <span className="text-title">{startIndex + 1} - {Math.min(endIndex, filteredPromoCodes.length)}</span> sur <span className="text-title">{filteredPromoCodes.length}</span> Entrées
                </div>

                <div className="flex items-center gap-2">
                   <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-text/40 hover:text-primary disabled:opacity-30 transition-all"><FontAwesomeIcon icon={faAngleDoubleLeft} /></button>
                   <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-text/40 hover:text-primary disabled:opacity-30 transition-all"><FontAwesomeIcon icon={faAngleLeft} /></button>
                   
                   <div className="flex gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)).map((page, i, arr) => (
                         <React.Fragment key={page}>
                            {i > 0 && arr[i-1] !== page - 1 && <span className="w-8 h-10 flex items-center justify-center text-text/20">...</span>}
                            <button onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-xl font-black text-[11px] transition-all ${currentPage === page ? 'bg-primary text-white shadow-lg' : 'bg-white border border-gray-100 text-text/40 hover:bg-gray-50'}`}>{page}</button>
                         </React.Fragment>
                      ))}
                   </div>

                   <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-text/40 hover:text-primary disabled:opacity-30 transition-all"><FontAwesomeIcon icon={faAngleRight} /></button>
                   <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-text/40 hover:text-primary disabled:opacity-30 transition-all"><FontAwesomeIcon icon={faAngleDoubleRight} /></button>
                </div>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default AdminPromoCodeManagement;


